import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { MasterdataApiService } from './masterdata-api.service';
import { SystemManufacturer } from '../utils/frame-brands.data';
import { calculateAreaWeightedUf } from '../utils/calculation';

@Injectable({
  providedIn: 'root',
})
export class WindowtypeUpdatesService {

  private readonly frameCounts: { [key: number]: number } = {
    1: 3, 2: 4, 3: 7, 4: 6, 5: 5, 6: 7,
    7: 5, 8: 6, 9: 3, 10: 4, 11: 5, 12: 7,
    13: 4, 14: 4, 15: 4, 16: 6
  };

  constructor(private storageService: StorageService, private masterdataApi: MasterdataApiService) { }

  async recalculateFrameUfValues(
    newWindowTypeId: number,
    isComparisonMode: boolean = false
  ): Promise<void> {

    const res = isComparisonMode
      ? await this.storageService.getComparedProject()
      : await this.storageService.getProject();

    const project = res ? JSON.parse(res) : null;
    if (!project?.windowframe) return;

    const frame = project.windowframe;
    const newFrameCount = this.frameCounts[newWindowTypeId] || 4;

    const existingUfValues: number[] = frame.ufValues ??
      Array.from({ length: 7 }, (_: any, i: number) => parseFloat(frame[`uf${i}`] || 0));

    const existingWValues: number[] = frame.wValues ??
      Array.from({ length: 7 }, (_: any, i: number) => parseFloat(frame[`w${i}`] || 0));

    const baseUf = parseFloat(frame.uf) || existingUfValues[0] || 1.1;
    const baseW = frame.visibleWidth || existingWValues[0] || 0.11;

    const newUfValues = Array.from({ length: 7 }, (_, i) => {
      if (i < newFrameCount) {
        return existingUfValues[i] > 0 ? existingUfValues[i] : baseUf;
      }
      return 0;
    });

    const newWValues = Array.from({ length: 7 }, (_, i) => {
      if (i < newFrameCount) {
        return existingWValues[i] > 0 ? existingWValues[i] : baseW;
      }
      return 0;
    });

    const updatedFrame = { ...frame, ufValues: newUfValues, wValues: newWValues };

    if (isComparisonMode) {
      this.storageService.updateComparedProject({ windowframe: updatedFrame });
    } else {
      this.storageService.updateProject({ windowframe: updatedFrame });
    }

    console.log('Recalculated ufValues for windowtype', newWindowTypeId, ':', newUfValues);
  }

  async autoUpdateByBrandFrame(
    newWindowTypeId: number,
    newGlazingValue: number,
    isComparisonMode: boolean = false
  ): Promise<void> {
    try {
      const res = isComparisonMode
        ? await this.storageService.getComparedProject()
        : await this.storageService.getProject();

      const project = res ? JSON.parse(res) : null;
      if (!project?.windowframe?.system) return;

      const { manufacturerId, systemId } = project.windowframe.system;
      if (!manufacturerId || !systemId) return;

      const cached = await this.masterdataApi.getCachedMasterData();
      const manufacturers: SystemManufacturer[] =
        cached?.frameManufacturers?.length
          ? cached.frameManufacturers as SystemManufacturer[]
          : [];

      const manufacturer = manufacturers.find(m => m.id === manufacturerId);
      if (!manufacturer) return;

      const system = manufacturer.systems.find(s => s.id === systemId);
      if (!system) return;

      const mapping = manufacturer.windowsMap.find(m => m.wid === newWindowTypeId);
      if (!mapping) {
        console.warn('No mapping for windowtype', newWindowTypeId);
        return;
      }

      const frameCount = this.frameCounts[newWindowTypeId] || 4;

      const glazingIndex = newGlazingValue - 1;
      const getUfValue = (uf: (number | null)[]): number => {
        if (glazingIndex >= 0 && glazingIndex < uf.length && uf[glazingIndex] !== null) {
          return uf[glazingIndex]!;
        }
        return 0;
      };

      const ufValues: number[] = new Array(7).fill(0);
      const wValues: number[] = new Array(7).fill(0);
      // let ufSum = 0, ufCount = 0;

      for (let i = 0; i < frameCount; i++) {
        const relIds = (mapping as any)[`f${i}`] as number[];
        if (!relIds || relIds.length === 0) continue;

        const profile = system.profiles.find(p => p.relIDs.includes(relIds[0]));
        if (!profile) continue;

        // const ufVal = getUfValue(profile.uf);
        // const wVal = profile.wf || 0;

        ufValues[i] = getUfValue(profile.uf);
        wValues[i] = profile.wf || 0;
        // if (ufVal > 0) { ufSum += ufVal; ufCount++; }
      }
      const dimensions = {
        a: parseFloat(project?.dimensions?.a || 0),
        b: parseFloat(project?.dimensions?.b || 0),
        c: parseFloat(project?.dimensions?.c || 0)
      };
      const frameWidths = {
        w0: wValues[0] || 0,
        w1: wValues[1] || 0,
        w2: wValues[2] || 0,
        w3: wValues[3] || 0,
        w4: wValues[4] || 0,
        w5: wValues[5] || 0,
        w6: wValues[6] || 0
      };
      const weightedUf = calculateAreaWeightedUf(
        newWindowTypeId,
        dimensions,
        frameWidths,
        ufValues
      );
      const updatedFrame = {
        ...project.windowframe,
        id: -1,
        type: system.frameType || 'FRAME_TYPE_PVC',
        uf: weightedUf,
        visibleWidth: wValues.find(w => w > 0) || 0.11,
        name: system.name,
        ufValues,
        wValues,
        system: { manufacturerId, systemId }
      };

      if (isComparisonMode) {
        await this.storageService.updateComparedProject({ windowframe: updatedFrame });
      } else {
        await this.storageService.updateProject({ windowframe: updatedFrame });
      }

      console.log('Auto-updated by-brand frame:', updatedFrame);
    } catch (error) {
      console.error('Error auto-updating by-brand frame:', error);
    }
  }
}
